import {
  checkUserOnboardingStatus,
  markOnboardingCompleted,
  clearOnboardingCache,
} from '../utils/onboardingCheck';
import { onboardingLogger } from '../utils/loggerInstance';
import { queryClient } from '../lib/queryClient';

import api from './apiService';

export interface OnboardingStatus {
  isCompleted: boolean;
  hasData: boolean;
  data?: {
    id: number;
    user_id: string;
    goals: string[];
    industries: string[];
    completed_at: string | null;
  };
}
/**
 * Onboarding Service
 * Handles checking and managing user onboarding status
 */
class OnboardingService {
  private readonly ONBOARDING_QUERY_KEY = 'onboarding-completed';

  private getQueryKey(userId: string): readonly [string, string] {
    return [this.ONBOARDING_QUERY_KEY, userId] as const;
  }

  getCachedCompletedOnboarding(userId?: string): boolean {
    if (!userId) return false;
    const cached = queryClient.getQueryData<boolean>(this.getQueryKey(userId));
    return cached === true;
  }

  /**
   * Determines if a user has completed onboarding through multiple reliable checks
   * Returns true if user has completed onboarding, false if they need to complete it
   *
   * This is the primary function used for navigation decisions after login
   * @param userId - Backend user ID from the authenticated session
   */
  async hasUserCompletedOnboarding(userId: string): Promise<boolean> {
    if (!userId) {
      onboardingLogger.error('OnboardingService: Missing user ID');
      throw new Error('OnboardingService: user ID is required');
    }

    // Check React Query cache first to prevent repeated API calls
    const queryCached = queryClient.getQueryData<boolean>(this.getQueryKey(userId));
    if (typeof queryCached === 'boolean') {
      return queryCached;
    }

    // Skip complex session validation - let navigation service handle routing
    // This prevents circular dependencies and conflicts with the optimized auth flow

    try {
      onboardingLogger.debug(
        'OnboardingService: Checking onboarding completion (lightweight API)',
        {
          userId,
        },
      );

      // STRATEGY 1: Try the lightweight API endpoint first (simplified to prevent loops)
      let simpleCheck = await checkUserOnboardingStatus();
      onboardingLogger.debug('OnboardingService: Simple API onboarding check result', {
        userId,
        simpleCheck,
      });

      if (simpleCheck === null) {
        onboardingLogger.warn(
          'OnboardingService: Inconclusive onboarding status, retrying once before failing',
          { userId },
        );
        simpleCheck = await checkUserOnboardingStatus();
      }

      if (simpleCheck === null) {
        throw new Error('OnboardingService: onboarding status inconclusive after retry');
      }

      queryClient.setQueryData(this.getQueryKey(userId), simpleCheck);
      return simpleCheck;

      // Removed complex fallback strategies to prevent API call loops
    } catch (error) {
      // This catch block handles errors from checkUserOnboardingStatus
      onboardingLogger.error('OnboardingService: Error in hasUserCompletedOnboarding', error);

      // Re-throw the error so calling components can handle it appropriately
      // Calling components can redirect to onboarding on error
      throw error;
    }
  }

  /**
   * Check if user has completed onboarding (with caching)
   * @param userId - Backend user ID from the authenticated session
   * @param useCache - Whether to use cached results
   * @returns OnboardingStatus object
   */
  async checkOnboardingStatus(userId: string, useCache: boolean = true): Promise<OnboardingStatus> {
    if (!userId) {
      onboardingLogger.error('OnboardingService: Missing user ID');
      return { isCompleted: false, hasData: false };
    }

    const cacheKey = `onboarding_status_${userId}`;

    if (useCache) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {
          onboardingLogger.warn('OnboardingService: Failed to parse cached onboarding status', {
            cacheKey,
            error: e,
          });
        }
      }
    }

    try {
      onboardingLogger.info('OnboardingService: Checking onboarding status for user:', {
        userId,
      });

      // Add a small delay to ensure token is properly stored
      await new Promise((resolve) => setTimeout(resolve, 100));

      const response = await api.get('/api/v1/users/onboarding');
      const data = response.data;

      const status: OnboardingStatus = {
        isCompleted: Boolean(data.completed_at),
        hasData: true,
        data: data,
      };

      sessionStorage.setItem(cacheKey, JSON.stringify(status));
      return status;
    } catch (error: unknown) {
      onboardingLogger.error('OnboardingService: Error checking onboarding status:', error);

      // Both 404 and other errors (401/403) return the same incomplete status
      // 404: user hasn't started onboarding yet
      // 401/403: authentication issue - return incomplete status
      const status: OnboardingStatus = { isCompleted: false, hasData: false };
      return status;
    }
  }
  /**
   * Get the appropriate redirect path based on onboarding status
   * @param userId - Backend user ID from the authenticated session
   * @returns Redirect path
   */
  async getRedirectPath(userId: string): Promise<string> {
    if (!userId) {
      onboardingLogger.error('OnboardingService: Missing user ID');
      return '/user-onboarding';
    }

    const hasCompleted = await this.hasUserCompletedOnboarding(userId);
    return hasCompleted ? '/chat' : '/user-onboarding';
  }
  /**
   * Check if the current path is an onboarding route
   */
  isOnboardingRoute(path: string): boolean {
    return (
      path.startsWith('/onboarding') ||
      path.startsWith('/user-onboarding') ||
      path.startsWith('/industry-selection')
    );
  }

  /**
   * Clear all cached onboarding data
   */
  clearAllCache(): void {
    onboardingLogger.info('OnboardingService: Clearing all cached data');
  }

  /**
   * Clear onboarding cache for a specific user
   */
  clearOnboardingCache(userId?: string): void {
    if (userId) {
      queryClient.removeQueries({ queryKey: this.getQueryKey(userId) });
    } else {
      queryClient.removeQueries({ queryKey: [this.ONBOARDING_QUERY_KEY] });
    }
    clearOnboardingCache();
    onboardingLogger.info('OnboardingService: Cleared onboarding cache');
  }

  /**
   * Mark onboarding as completed (called after user completes onboarding)
   */
  markOnboardingCompleted(userId: string): void {
    if (!userId) {
      onboardingLogger.warn('OnboardingService: Cannot mark onboarding completed without user ID');
      return;
    }
    queryClient.setQueryData(this.getQueryKey(userId), true);
    markOnboardingCompleted();
    onboardingLogger.info('OnboardingService: Marked onboarding as completed', {
      userId,
    });
  }

  /**
   * Check if user is a returning user (has completed onboarding)
   * @param userId - Backend user ID from the authenticated session
   * @returns True if returning user
   */
  async isReturningUser(userId: string): Promise<boolean> {
    if (!userId) {
      onboardingLogger.error('OnboardingService: Missing user ID');
      return false;
    }

    return await this.hasUserCompletedOnboarding(userId);
  }
}

export const onboardingService = new OnboardingService();

export default onboardingService;
